/**
 * What the narrator says, in order, one utterance at a time.
 *
 * An article is read as blocks (its title, the reader's intro line, the lede,
 * then every heading, paragraph, list item and caption of the body), and each
 * block is split into sentence-sized chunks. Chunks are what the browser's
 * speech synthesis receives, one at a time, because:
 *   - Chrome silently stops any utterance longer than about fifteen seconds
 *     when the voice is one of Google's remote ones;
 *   - resuming after an interruption lands on the sentence that was cut, not
 *     at the top of a paragraph;
 *   - the paragraph being read can be marked on the page.
 *
 * Pure: no DOM import. The collector reads a `NarratorNode`, which a real
 * Element satisfies and a test can build from plain objects.
 */

export type NarratorBlockKind =
  | 'title'
  | 'intro'
  | 'lede'
  | 'heading'
  | 'paragraph'
  | 'item'
  | 'caption'
  | 'line';

/** The part of an Element the collector reads. */
export interface NarratorNode {
  tagName: string;
  textContent: string | null;
  children: ArrayLike<NarratorNode>;
  id?: string;
}

export interface NarratorBlock<N = NarratorNode> {
  kind: NarratorBlockKind;
  text: string;
  /** The element to mark while this block is read; none for spoken-only lines. */
  el?: N | null;
}

export interface NarratorChunk {
  /** Index into the block list this chunk belongs to. */
  block: number;
  text: string;
}

/** Longest utterance handed to speech synthesis, in characters. */
export const MAX_CHUNK_CHARS = 200;

const NBSP = /[   ]/g;
/** A link written out in the text. One match: the speech route refuses a text that has one. */
export const URL_PATTERN = /\bhttps?:\/\/\S+/i;
/** Pictographs and symbols a voice would otherwise name aloud ("red heart"). One match, like URL_PATTERN. */
export const EMOJI_PATTERN = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/u;
const URL_RE = new RegExp(URL_PATTERN.source, 'gi');
const EMOJI_RE = new RegExp(EMOJI_PATTERN.source, 'gu');
const LEADING_BULLET = /^[\s•·\-–—*]+/;

/** Text a voice can read: no bullets, URLs or pictographs, single spaces. */
export function cleanForSpeech(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(NBSP, ' ')
    .replace(URL_RE, '')
    .replace(EMOJI_RE, '')
    .replace(LEADING_BULLET, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const TAGS_READ_AS_HEADING = new Set(['H2', 'H3', 'H4']);
const TAGS_READ_AS_PARAGRAPH = new Set(['P', 'BLOCKQUOTE', 'PRE']);
const TAGS_DESCENDED = new Set(['DIV', 'SECTION', 'ARTICLE', 'MAIN']);

/**
 * The body of an article as blocks, in reading order. Skips the H1 (the
 * title is read first, from the page), images, charts, tables, navigation and
 * anything empty. Walks into plain containers, since some articles wrap their
 * body in a div.
 */
export function collectArticleBlocks<N extends NarratorNode>(root: N | null | undefined): NarratorBlock<N>[] {
  const out: NarratorBlock<N>[] = [];
  if (!root) return out;
  const visit = (node: N) => {
    const children = Array.from(node.children as ArrayLike<N>);
    for (const child of children) {
      const tag = (child.tagName || '').toUpperCase();
      if (TAGS_READ_AS_HEADING.has(tag)) {
        push(out, 'heading', child);
      } else if (TAGS_READ_AS_PARAGRAPH.has(tag)) {
        push(out, 'paragraph', child);
      } else if (tag === 'UL' || tag === 'OL') {
        for (const item of Array.from(child.children as ArrayLike<N>)) {
          if ((item.tagName || '').toUpperCase() === 'LI') push(out, 'item', item);
        }
      } else if (tag === 'FIGURE') {
        for (const part of Array.from(child.children as ArrayLike<N>)) {
          if ((part.tagName || '').toUpperCase() === 'FIGCAPTION') push(out, 'caption', part);
        }
      } else if (TAGS_DESCENDED.has(tag) && child.id !== 'chart') {
        visit(child);
      }
    }
  };
  visit(root);
  return out;
}

function push<N extends NarratorNode>(out: NarratorBlock<N>[], kind: NarratorBlockKind, el: N) {
  const text = cleanForSpeech(el.textContent);
  if (text) out.push({ kind, text, el });
}

const SENTENCE_END = new Set(['.', '!', '?', '…']);
const CJK_SENTENCE_END = new Set(['。', '！', '？']);
const CLOSERS = new Set(['"', "'", '”', '’', ')', ']', '»', '」', '』']);
// A period that ends one of these is not the end of a sentence.
const ABBREVIATION_RE =
  /(?:^|[\s(])(?:e\.g|i\.e|etc|vs|cf|approx|no|nos|mr|mrs|ms|dr|st|jr|sr|prof|p\.ex|m|mme|mlle|sra|sr|dra|ud|uds|z\.b|bzw|usw|ca|hr|fr|nr|s|inkl|exkl|[a-z])\.$/i;
/** Shorter than this, a fragment joins the sentence before it. */
const MIN_FRAGMENT_CHARS = 24;
const CJK_RE = /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af]/;
/** A character in a script without spaces carries about four Latin characters' worth. */
const fragmentWeight = (s: string) => (CJK_RE.test(s) ? s.length * 4 : s.length);

/**
 * Sentences of a text, each at most `maxLen` characters. Decimal points,
 * common abbreviations and closing quotes do not end a sentence; a sentence
 * longer than `maxLen` breaks at its last comma, semicolon or colon before
 * the limit, else at a space. Works without spaces too (Japanese ends on 。！？).
 */
export function splitSentences(text: string, maxLen: number = MAX_CHUNK_CHARS): string[] {
  const cleaned = cleanForSpeech(text);
  if (!cleaned) return [];
  const chars = Array.from(cleaned);
  const sentences: string[] = [];
  let buf = '';
  const flush = () => {
    const s = buf.trim();
    if (s) sentences.push(s);
    buf = '';
  };
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    buf += ch;
    if (CJK_SENTENCE_END.has(ch)) {
      flush();
      continue;
    }
    if (!SENTENCE_END.has(ch)) continue;
    // Swallow a run of terminators ("?!", "...") and closing quotes.
    let j = i + 1;
    while (j < chars.length && (SENTENCE_END.has(chars[j]) || CLOSERS.has(chars[j]))) {
      buf += chars[j];
      j++;
    }
    const atEnd = j >= chars.length;
    const beforeSpace = !atEnd && /\s/.test(chars[j]);
    if ((atEnd || beforeSpace) && !ABBREVIATION_RE.test(buf.trimEnd())) flush();
    i = j - 1;
  }
  flush();
  return breakLong(mergeShort(sentences), maxLen);
}

function mergeShort(sentences: string[]): string[] {
  const out: string[] = [];
  for (const s of sentences) {
    const prev = out[out.length - 1];
    if (prev !== undefined && (fragmentWeight(s) < MIN_FRAGMENT_CHARS || fragmentWeight(prev) < MIN_FRAGMENT_CHARS)) {
      const glue = CJK_SENTENCE_END.has(prev[prev.length - 1]) ? '' : ' ';
      out[out.length - 1] = `${prev}${glue}${s}`;
    } else {
      out.push(s);
    }
  }
  return out;
}

function breakLong(sentences: string[], maxLen: number): string[] {
  const out: string[] = [];
  for (const s of sentences) {
    let rest = s;
    while (rest.length > maxLen) {
      const window = rest.slice(0, maxLen);
      let cut = -1;
      for (const sep of ['; ', ': ', ', ', '、', '，', ' ']) {
        const at = window.lastIndexOf(sep);
        if (at > maxLen / 3) {
          cut = at + (sep === ' ' ? 0 : sep.length - 1);
          break;
        }
      }
      if (cut <= 0) cut = maxLen;
      out.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    if (rest) out.push(rest);
  }
  return out;
}

/**
 * Consecutive sentences joined into pieces of at most `maxLen` characters.
 * A rendered voice reads a paragraph more naturally than a string of single
 * sentences, since each request starts its intonation afresh.
 */
export function packSentences(sentences: string[], maxLen: number): string[] {
  const out: string[] = [];
  for (const sentence of sentences) {
    const prev = out[out.length - 1];
    if (prev !== undefined && prev.length + 1 + sentence.length <= maxLen) {
      const glue = CJK_SENTENCE_END.has(prev[prev.length - 1]) ? '' : ' ';
      out[out.length - 1] = `${prev}${glue}${sentence}`;
    } else {
      out.push(sentence);
    }
  }
  return out;
}

/** The longest piece handed to the rendered voice: about forty seconds of speech. */
export const MAX_RENDERED_CHUNK_CHARS = 600;

/**
 * Every block as chunks, in order. Headings and the title stay whole. With
 * `pack`, a paragraph's sentences are joined up to `maxLen` (the rendered
 * voice); without it each chunk is one sentence (the device's voice).
 */
export function chunkBlocks<N>(
  blocks: NarratorBlock<N>[],
  maxLen: number = MAX_CHUNK_CHARS,
  pack: boolean = false,
): NarratorChunk[] {
  const chunks: NarratorChunk[] = [];
  blocks.forEach((block, index) => {
    const whole = block.kind === 'heading' || block.kind === 'title' || block.kind === 'intro';
    const sentences = whole ? [cleanForSpeech(block.text)] : splitSentences(block.text, maxLen);
    const parts = pack && !whole ? packSentences(sentences, maxLen) : sentences;
    for (const text of parts) if (text) chunks.push({ block: index, text });
  });
  return chunks;
}
